import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { SectorService } from '../../../core/services/sector.service';
import { Sector } from '../../../core/models/sector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SectorFormComponent } from './sector-form/sector-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-sector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective
  ],
  templateUrl: './sector.component.html',
  styleUrl: './sector.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class SectorComponent {
  sectors!: Sector[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private sectorService: SectorService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllSectors();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.sector.addSector.title',
        'layout.configurations.sector.editSector.title',
        'layout.configurations.sector.editSector.name',
        'layout.configurations.sector.addSector.successMessage.title',
        'layout.configurations.sector.addSector.successMessage.detail',
        'layout.configurations.sector.editSector.successMessage.title',
        'layout.configurations.sector.editSector.successMessage.detail',
        'layout.configurations.sector.deleteSector.title',
        'layout.configurations.sector.deleteSector.message',
        'layout.configurations.sector.deleteSector.yes',
        'layout.configurations.sector.deleteSector.no',
        'layout.configurations.sector.deleteSector.successMessage.title',
        'layout.configurations.sector.deleteSector.successMessage.detail',
        'layout.configurations.sector.deleteSector.errorMessage.title',
        'layout.configurations.sector.deleteSector.errorMessage.detail',
        'layout.configurations.sector.deleteSector.infoMessage.title',
        'layout.configurations.sector.deleteSector.infoMessage.detail',
        'layout.configurations.sector.name',
        'layout.configurations.sector.actions',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllSectors() {
    this.sectorService.getSectors().subscribe((data) => (this.sectors = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(SectorFormComponent, {
      header: this.translations['layout.configurations.sector.addSector.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((sector) => {
      if (sector) {
        this.getAllSectors();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.sector.addSector.successMessage.title'],
          detail: this.translations['layout.configurations.sector.addSector.successMessage.detail'],
        });
      }
    });
  }

  onEdit(sector: Sector) {
    this.ref = this.dialogService.open(SectorFormComponent, {
      header: this.translations['layout.configurations.sector.editSector.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: sector,
    });

     this.ref.onClose.subscribe((result) => {
    if (!result) return;

    if (result.type === 'updated') {
        this.getAllSectors();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.sector.editSector.successMessage.title'],
          detail: this.translations['layout.configurations.sector.editSector.successMessage.detail'],
        });
      }
      else if (result.type === 'no-change') {
        this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
       }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.sector.deleteSector.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.sector.deleteSector.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.sector.deleteSector.yes'],
      rejectLabel: this.translations['layout.configurations.sector.deleteSector.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteSector(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
         summary: this.translate.instant('layout.configurations.sector.deleteSector.info.title'),
          detail: this.translate.instant('layout.configurations.sector.deleteSector.info.detail'),

          life: 3000,
        });
      },
    });
  }

  deleteSector(id: number) {
    this.sectorService.deleteSector(id).subscribe({
      next: () => {
        this.getAllSectors();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.sector.deleteSector.successMessage.title'],
          detail: this.translations['layout.configurations.sector.deleteSector.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting sector: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.sector.deleteSector.errorMessage.title'],
          detail: this.translations['layout.configurations.sector.deleteSector.errorMessage.detail'],
        });
      },
    });
  }
}
